package com.iregained.site.components.sections.clinicians.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.clinicians.styles.*
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.SubheadlineTextStyle
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ul

// UNIQUE ANIMATION: Vertical Split Converge
@Composable
private fun Modifier.verticalSplit(isVisible: Boolean, slideDown: Boolean, delayMs: Int): Modifier {
    val startY = if (slideDown) (-30).px else 30.px
    return this.opacity(if (isVisible) 1 else 0).translateY(if (isVisible) 0.px else startY).transition(
        Transition.of("opacity", 0.7.s, TransitionTimingFunction.EaseOut, delayMs.ms),
        Transition.of("transform", 0.7.s, TransitionTimingFunction.EaseOut, delayMs.ms)
    )
}

@Composable
fun CliniciansTargetedMessagingSection(data: CliniciansTargetedMessagingData = defaultCliniciansTargetedMessagingData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()
    var selectedTabIndex by remember { mutableStateOf(0) }
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "clinicians-targeted-messaging-section"
    var isVisible by remember { mutableStateOf(false) }

    if (data.tabs.isNotEmpty()) {
        Column(
            modifier = TargetedMessagingContainerStyle.toModifier().id(targetId).fillMaxWidth()
                .backgroundColor(palette.brand.primarySoft)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.Center)
                    .toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element); onDispose { observer.disconnect() }
                        }
                    }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1440.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    if (data.sectionTitle.isNotBlank()) SectionHeader(
                        text = data.sectionTitle,
                        fontSize = 32.px,
                        lineWidthPercent = 25.percent,
                        modifier = Modifier.margin(bottom = 16.px).opacity(if (isVisible) 1 else 0)
                            .transition(Transition.of("opacity", 0.8.s)),
                        alignment = Alignment.CenterHorizontally
                    )
                    if (data.sectionSubtitle.isNotBlank()) Div(
                        attrs = SubheadlineTextStyle.toModifier().maxWidth(1200.px).fontSize(18.px)
                            .fontWeight(FontWeight.Medium).color(palette.custom.darkText).textAlign(TextAlign.Center)
                            .margin(bottom = 40.px).opacity(if (isVisible) 1 else 0)
                            .transition(Transition.of("opacity", 0.8.s, delay = 150.ms)).toAttrs {
                                ref { htmlElement ->
                                    htmlElement.innerHTML = data.sectionSubtitle; onDispose {}
                                }
                            })

                    Column(modifier = Modifier.fillMaxWidth().maxWidth(1000.px)) {
                        Row(
                            modifier = TargetedTabBarContainerStyle.toModifier().opacity(if (isVisible) 1 else 0)
                                .transition(Transition.of("opacity", 0.8.s, delay = 300.ms)).flexWrap(FlexWrap.Wrap),
                            horizontalArrangement = Arrangement.Center
                        ) {
                            data.tabs.forEachIndexed { index, tab ->
                                key(tab.tabTitle) {
                                    val isSelected = selectedTabIndex == index
                                    Box(
                                        modifier = TargetedTabItemStyle.toModifier().then(
                                            if (isSelected) Modifier.borderBottom(
                                                3.px, LineStyle.Solid, palette.brand.primary
                                            ) else Modifier
                                        ).onClick { selectedTabIndex = index }, contentAlignment = Alignment.Center
                                    ) {
                                        SpanText(
                                            text = tab.tabTitle,
                                            modifier = Modifier.fontSize(18.px)
                                                .fontWeight(if (isSelected) FontWeight.Bold else FontWeight.Medium)
                                                .color(
                                                    if (isSelected) palette.brand.primary else palette.custom.darkText.toRgb()
                                                        .copyf(alpha = 0.6f)
                                                ).transition(Transition.of("color", 200.ms))
                                        )
                                    }
                                }
                            }
                        }

                        val currentTab = data.tabs[selectedTabIndex]
                        SimpleGrid(numColumns(base = 1, sm = 1, md = 2), modifier = TargetedGridStyle.toModifier()) {
                            Column(
                                modifier = TargetedContentColumnStyle.toModifier().verticalSplit(isVisible, true, 450),
                                verticalArrangement = Arrangement.Center
                            ) {
                                if (currentTab.heading.isNotBlank()) SpanText(
                                    text = currentTab.heading, modifier = TargetedHeadingStyle.toModifier()
                                )
                                if (currentTab.description.isNotBlank()) Div(
                                    attrs = TargetedDescStyle.toModifier().toAttrs {
                                        ref { htmlElement ->
                                            htmlElement.innerHTML = currentTab.description; onDispose {}
                                        }
                                    })
                                if (currentTab.bulletPoints.isNotEmpty()) Div {
                                    Ul(
                                        attrs = TargetedUlStyle.toModifier().toAttrs()
                                    ) {
                                        currentTab.bulletPoints.forEach { point ->
                                            key(point) {
                                                Li(
                                                    attrs = TargetedLiStyle.toModifier().toAttrs()
                                                ) {
                                                    SpanText(
                                                        text = point,
                                                        modifier = TargetedLiTextStyle.toModifier()
                                                    )
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            Box(modifier = TargetedImageBoxStyle.toModifier().verticalSplit(isVisible, false, 450)) {
                                if (currentTab.imagePath.isNotBlank()) Image(
                                    src = currentTab.imagePath, modifier = TargetedImageStyle.toModifier()
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

// ==========================================
// CMS DATA MODEL & IMAGE SPECS
// ==========================================
/**
 * CMS IMAGE UPLOAD SPECS FOR TARGETED MESSAGING TABS:
 * @field imagePath:
 * - Aspect Ratio: 4:3 or 1:1 (Square).
 * - Recommended Dimensions: 800x800px.
 * - Format: WebP or high-quality JPEG.
 * - Content: The image must match the persona. E.g., For Administrators, show a wide shot of a modern clinic. For OTs, show a close-up of a patient performing an ADL task with the device.
 */
data class CliniciansTabItemData(
    val tabTitle: String, // e.g., "For Administrators", "For Physical Therapists"
    val heading: String, val description: String, val bulletPoints: List<String>, val imagePath: String
)

data class CliniciansTargetedMessagingData(
    val sectionId: String? = null,
    val sectionTitle: String = "BUILT FOR YOUR ENTIRE TEAM",
    val sectionSubtitle: String = "See how the MyHand<sup>®</sup> System delivers specific, measurable value to every role in your facility.",
    val tabs: List<CliniciansTabItemData> = listOf(
        CliniciansTabItemData(
            tabTitle = "Clinic Owners & Admins",
            heading = "Maximize ROI and Patient Throughput",
            description = "Differentiate your facility with cutting-edge rehab technology. The MyHand<sup>®</sup> System allows for higher patient volume without increasing the physical strain on your staff.",
            bulletPoints = listOf(
                "Expand billable rehabilitation services",
                "Attract new patients with advanced DTx marketing",
                "Reduce therapist burnout and turnover"
            ),
            imagePath = Res.Images.CliniciansPage.TARGET.OWNER_ADMINS
        ),
        CliniciansTabItemData(
            tabTitle = "Therapists",
            heading = "Evidence-Based Gross Motor Recovery",
            description = "Integrate neuroplasticity-focused protocols into your daily sessions. Track objective baseline metrics and monitor gross motor improvement over time.",
            bulletPoints = listOf(
                "Automated progression tracking",
                "Reduces heavy physical lifting during therapy",
                "Customizable resistance and assistance profiles"
            ),
            imagePath = Res.Images.CliniciansPage.TARGET.PT
        ),
//        CliniciansTabItemData(
//            tabTitle = "Occupational Therapists",
//            heading = "Translate Therapy into Daily Independence",
//            description = "Focus on the fine motor skills that matter. Our gamified tasks mimic Activities of Daily Living (ADLs) to keep patients cognitively engaged.",
//            bulletPoints = listOf(
//                "High-repetition task-specific training",
//                "Improves grip, pinch, and grasp mechanics",
//                "Highly engaging for stroke survivors"
//            ),
//            imagePath = "/images/clinicians/tabs/ot_view.jpg"
//        )
    )
)

val defaultCliniciansTargetedMessagingData = CliniciansTargetedMessagingData()